import type { DateTime } from "luxon";
import { compareValues } from "../../../utils/multiColumnSorting";
import type { PrintoutColumn, PrintoutColumnType } from "../types";

interface SortHorizontalAxisArgs {
	/** Distinct horizontal-axis values in their pre-sort encounter order (may be alias-collapsed). */
	values: string[];
	/** The column kind that defines the pivot axis. */
	columnGroupingBy: PrintoutColumnType;
	/**
	 * For shift-time pivots, a map from each display value to the representative `DateTime`
	 * that should drive its sort position. Ignored for other column kinds.
	 */
	shiftTimeReprs?: Map<string, DateTime>;
	/** The user's *Sort by* list — read only to honor direction on the pivot column (mapping §8.2). */
	sortBy: PrintoutColumn[] | undefined;
	/** Custom location/floor order map; participates only when the pivot column is location/floor. */
	customStringOrderMap: Map<string, number> | null | undefined;
}

/**
 * Order the horizontal-axis values for a pivot layout (mapping §8.2 / §3f).
 *
 * Uses the shared `compareValues` — no bespoke per-column-kind comparator. The value type fed
 * into `compareValues` depends on the pivot column:
 * - **Shift Time** → the representative `DateTime` from `shiftTimeReprs` (compareValues step 5).
 * - **Location / Floor** → the display string; the chain naturally applies `customStringOrderMap`
 *   (step 7) and then the floor heuristic (step 8) before falling through to natural string.
 * - **Other** → the display string; natural string compare (step 9) dominates.
 *
 * Direction (`asc` / `desc`) is honored only if *Sort by* contains an enabled entry for the same
 * column kind as the pivot axis.
 *
 * **Single-axis assumption.** This function relies on `columnGroupingBy` being **exactly one**
 * column kind (mapping §6.1 / §8.2 — "Horizontal pivot — One column kind (or none)"). That's why
 * direction can be a single multiplier applied to one `compareValues` call: there is one pivot
 * axis, one direction. If we ever support multi-axis pivots (`columnGroupingBy: PrintoutColumnType[]`),
 * this function must be rewritten to dispatch through `multiSortComparator` with one `SortingState`
 * per axis — a single `directionMultiplier` would silently apply only the first axis's direction
 * to every comparison.
 */
export const sortHorizontalAxis = (args: SortHorizontalAxisArgs): string[] => {
	const { values, columnGroupingBy, shiftTimeReprs, sortBy, customStringOrderMap } = args;

	const sortDirection = sortBy?.find((c) => c.enabled && c.type === columnGroupingBy)?.sortDirection;
	const directionMultiplier = sortDirection === "desc" ? -1 : 1;

	const valueFor = (display: string): unknown => {
		if (columnGroupingBy === "shiftTime") return shiftTimeReprs?.get(display) ?? display;
		return display;
	};

	const sorted = [...values];
	sorted.sort((a, b) => {
		const cmp = compareValues(valueFor(a), valueFor(b), customStringOrderMap);
		return directionMultiplier * cmp;
	});
	return sorted;
};
